// Shuffle Card full-page view.
//
// Reached by tapping the Shuffle chip in the scorecard chip strip.
// Renders the full ShuffleCard component in `variant="full"` — history
// expanded by default, deck count, active effects, and the picker modal
// still fires when picks_remaining > 0 (so the page IS a valid pick
// surface too, not just a review view).
//
// Two navigation shapes:
//   /shuffle              → picks the first active shuffle_card game on
//                           the caller's current standalone round (from
//                           localStorage active_round). Usable as the
//                           default deep-link when the chip strip has
//                           no game_id to disambiguate.
//   /shuffle?game=<id>    → renders that specific game (used by the
//                           chip strip's onChipTap handler which knows
//                           the game_id).
//
// Edit / End (Jacob 2026-07-14): the /shuffle page owns the mid-game
// controls that used to live in the scorecard's Side Games box.
//   * Edit: toggle Shuffle mode (locked once cards have been drawn —
//     server returns mode_locked and the toast surfaces it).
//   * End Game: finalize the game via end_game; navigates back to the
//     scorecard on success.

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import ShuffleCard from '../components/ShuffleCard'

// Same defensive parse the scorecard uses. Inlined to avoid pulling the
// whole ScorecardPage import surface into this route.
function safeParse(raw, fallback) {
  try { const p = JSON.parse(raw); return p ?? fallback } catch { return fallback }
}

async function callStandalone(action, payload) {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('Please sign in again.')
  const res = await fetch('/.netlify/functions/standalone-game', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ action, ...payload }),
  })
  const json = await res.json()
  if (!res.ok) {
    const err = new Error(json.error || `${action} failed`)
    err.code = json.code
    throw err
  }
  return json.data
}

// Match the scorecard's localStorage key format. ScorecardPage writes
// `{scores, holeStats, currentHole}` to this key on every save + hole
// change, so a poll on visibility gives /shuffle a live-enough view of
// the scorekeeper's current hole.
function scorecardKeyFor(playerId, roundId) {
  return `standalone_round_${playerId || 'anon'}_${roundId || 'anon'}`
}

export default function ShufflePage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { player } = useAuth()

  // Which game to render — from ?game=<id> if present, else discovered
  // by list_games on the caller's active standalone round.
  const gameIdFromQuery = useMemo(() => {
    const p = new URLSearchParams(location.search)
    return p.get('game') || null
  }, [location.search])

  const [gameId, setGameId] = useState(gameIdFromQuery)
  const [standaloneRoundId, setStandaloneRoundId] = useState(null)
  const [activeRound, setActiveRound] = useState(null)
  const [currentHole, setCurrentHole] = useState(1)
  const [holePar, setHolePar] = useState(4)
  const [err, setErr] = useState(null)
  const [toast, setToast] = useState(null)
  const [loading, setLoading] = useState(true)
  const [editMode, setEditMode] = useState(false)
  const [config, setConfig] = useState(null)
  const [busy, setBusy] = useState(false)

  // Sync current-hole + par from the scorecard's localStorage. The
  // scorecard writes `{currentHole}` on every save AND on every hole
  // navigation, so an interval poll (every ~1s) keeps /shuffle within
  // one tick of the scorecard even without event plumbing.
  const syncFromLocalStorage = useCallback(() => {
    try {
      const raw = localStorage.getItem('active_round')
      const ar = safeParse(raw, null)
      if (ar?.id) {
        setStandaloneRoundId(ar.id)
        setActiveRound(ar)
      }
      if (ar?.id && player?.id) {
        const key = scorecardKeyFor(player.id, ar.id)
        const saved = safeParse(localStorage.getItem(key), null)
        const savedHole = Number(saved?.currentHole)
        if (Number.isFinite(savedHole) && savedHole >= 1) {
          setCurrentHole(prev => (prev === savedHole ? prev : savedHole))
          if (ar.course?.holes_data && Array.isArray(ar.course.holes_data)) {
            const h = ar.course.holes_data.find(x => x.hole_number === savedHole)
            if (h?.par) setHolePar(h.par)
          }
        }
      }
    } catch {}
  }, [player?.id])

  useEffect(() => {
    syncFromLocalStorage()
    const id = setInterval(syncFromLocalStorage, 1000)
    return () => clearInterval(id)
  }, [syncFromLocalStorage])

  // Auto-discover the game_id when the query param is absent — pick the
  // first active shuffle_card game on the current standalone round.
  useEffect(() => {
    if (gameId || !standaloneRoundId) { setLoading(false); return }
    (async () => {
      try {
        const data = await callStandalone('list_games', { standalone_round_id: standaloneRoundId })
        const active = (data || []).filter(g => !g.ended_at && g.format === 'shuffle_card')
        if (active.length > 0) {
          setGameId(active[0].id)
          setConfig(active[0].config || {})
        } else setErr('No active Shuffle Card game on this round.')
      } catch (e) {
        setErr(e.message)
      }
      setLoading(false)
    })()
  }, [gameId, standaloneRoundId])

  // Load the game's current config so the Edit form has the current
  // shuffle_mode selected. Runs whenever gameId changes; also called
  // after a successful save.
  const loadConfig = useCallback(async () => {
    if (!standaloneRoundId || !gameId) return
    try {
      const data = await callStandalone('list_games', { standalone_round_id: standaloneRoundId })
      const g = (data || []).find(x => x.id === gameId)
      if (g) setConfig(g.config || {})
    } catch {}
  }, [gameId, standaloneRoundId])
  useEffect(() => { loadConfig() }, [loadConfig])

  const showToast = (msg) => {
    setToast(msg)
    setTimeout(() => setToast(null), 3500)
  }

  const saveMode = async (mode) => {
    setBusy(true)
    try {
      const data = await callStandalone('update_game_config', {
        game_id: gameId,
        config: { shuffle_mode: mode },
      })
      setConfig(data?.config || { ...config, shuffle_mode: mode })
      showToast(`Mode: ${mode.replace('_', ' ')}`)
    } catch (e) {
      showToast(e.message)
    }
    setBusy(false)
  }

  const endGame = async () => {
    if (!window.confirm('End this Shuffle Card game? Final state stays visible; no more picks.')) return
    setBusy(true)
    try {
      await callStandalone('end_game', { game_id: gameId })
      showToast('Game ended.')
      setTimeout(() => navigate(-1), 800)
    } catch (e) {
      showToast(e.message)
    }
    setBusy(false)
  }

  const currentMode = config?.shuffle_mode || 'straight_up'

  return (
    <div className="page">
      <div className="container">
        <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            onClick={() => navigate(-1)}
            aria-label="Back"
            style={{
              width: 36, height: 36, borderRadius: 6,
              border: '1px solid var(--green-mid)',
              background: 'var(--green-deep)', color: 'var(--cream)',
              fontSize: '1.2rem', cursor: 'pointer',
            }}
          >
            ‹
          </button>
          <div style={{ flex: 1 }}>
            <p className="eyebrow">Side Game</p>
            <h1>Shuffle Card</h1>
          </div>
          {gameId && (
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={() => setEditMode(m => !m)}
                style={{
                  padding: '6px 12px', border: '1px solid var(--gold)',
                  background: 'transparent', color: 'var(--gold)',
                  borderRadius: 6, fontSize: '0.8rem', cursor: 'pointer',
                }}
              >
                {editMode ? 'Close' : 'Edit'}
              </button>
              <button
                onClick={endGame}
                disabled={busy}
                style={{
                  padding: '6px 12px', border: '1px solid var(--gray-500)',
                  background: 'transparent', color: 'var(--gray-300)',
                  borderRadius: 6, fontSize: '0.8rem', cursor: busy ? 'not-allowed' : 'pointer',
                  opacity: busy ? 0.6 : 1,
                }}
              >
                End
              </button>
            </div>
          )}
        </div>

        {editMode && gameId && (
          <div className="card card-sm" style={{ marginTop: 8 }}>
            <p style={{ fontSize: '0.78rem', color: 'var(--gray-300)', margin: '0 0 8px' }}>
              Mode (locked once cards have been drawn):
            </p>
            <div style={{ display: 'grid', gap: 4 }}>
              {[
                { v: 'straight_up', label: 'Straight Up', desc: '1 card per group per hole' },
                { v: 'double_down', label: 'Double Down', desc: '2 cards per group per hole; effects stack' },
                { v: 'duel',        label: 'Duel',        desc: '2 sides, shared deck' },
              ].map(m => (
                <label
                  key={m.v}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 6,
                    padding: 8, borderRadius: 6,
                    background: currentMode === m.v ? 'rgba(212,185,122,0.12)' : 'transparent',
                    border: `1px solid ${currentMode === m.v ? 'var(--gold)' : 'var(--green-mid)'}`,
                    cursor: busy ? 'not-allowed' : 'pointer',
                  }}
                >
                  <input
                    type="radio"
                    checked={currentMode === m.v}
                    onChange={() => !busy && saveMode(m.v)}
                    disabled={busy}
                    style={{ marginTop: 2 }}
                  />
                  <span style={{ flex: 1 }}>
                    <span style={{ fontWeight: 600 }}>{m.label}</span>
                    <span style={{ display: 'block', color: 'var(--gray-400)', fontSize: '0.72rem' }}>{m.desc}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        {toast && (
          <div className="card card-sm" style={{ marginTop: 8, fontSize: '0.82rem' }}>
            {toast}
          </div>
        )}

        {loading && (
          <p className="text-muted text-sm">Loading Shuffle…</p>
        )}
        {err && !gameId && (
          <div className="card card-sm" style={{ marginTop: 12 }}>
            <p style={{ margin: 0, color: 'var(--red)' }}>{err}</p>
          </div>
        )}
        {gameId && (
          <div className="card" style={{ marginTop: 12 }}>
            <ShuffleCard
              game_id={gameId}
              viewerPlayerId={player?.id}
              viewerCurrentHole={currentHole}
              currentHolePar={holePar}
              variant="full"
            />
          </div>
        )}
      </div>
    </div>
  )
}
